import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { sortedPosts, formatDate } from '../data/blogPosts';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/meewwwbb';
const PER_PAGE = 6;

const css = `
  .blog-root{
    --blue: #0072F5;
    --blue-dark: #0052B3;
    --ink: #0B0D10;
    --paper: #F6F4EF;
    --paper-dim: #ECE8DF;
    --line: rgba(11,13,16,0.12);
    background:var(--paper);
    color:var(--ink);
    font-family:'Inter',sans-serif;
    -webkit-font-smoothing:antialiased;
    min-height:100vh;
    display:flex;
    flex-direction:column;
  }
  .blog-root *{box-sizing:border-box;}
  .blog-root .serif{font-family:'Newsreader', serif; font-weight:400;}
  .blog-root .wrap{max-width:1240px; margin:0 auto; padding:0 32px;}
  .blog-root a{color:inherit; text-decoration:none;}

  .blog-root header{
    position:fixed; top:0; left:0; right:0; z-index:90;
    background:rgba(246,244,239,0.92); backdrop-filter:blur(10px);
    border-bottom:1px solid var(--line);
  }
  .blog-root nav{
    display:flex; align-items:center; justify-content:space-between;
    padding:20px 32px; max-width:1240px; margin:0 auto;
  }
  .blog-root .logo{font-weight:800; font-size:18px; letter-spacing:-0.02em;}
  .blog-root .logo span{color:var(--blue);}
  .blog-root .nav-links{display:flex; gap:36px; font-size:14px; font-weight:500;}
  .blog-root .nav-links a{opacity:.7; transition:opacity .2s;}
  .blog-root .nav-links a:hover{opacity:1;}
  .blog-root .nav-actions{display:flex; align-items:center; gap:22px;}
  .blog-root .nav-login{font-size:14px; font-weight:600; opacity:.75; transition:opacity .2s;}
  .blog-root .nav-login:hover{opacity:1;}
  .blog-root .nav-cta{
    font-size:14px; font-weight:600; padding:10px 20px;
    border:1px solid var(--ink); border-radius:2px;
    transition:background .2s, color .2s;
  }
  .blog-root .nav-cta:hover{background:var(--ink); color:var(--paper);}
  .blog-root .nav-toggle{display:none; background:none; border:none; cursor:pointer; padding:8px; margin-left:8px;}
  .blog-root .nav-toggle span{display:block; width:20px; height:2px; background:var(--ink); margin:4px 0;}

  .blog-root main{flex:1;}

  .blog-root .blog-hero{padding:150px 32px 20px;}
  .blog-root .eyebrow{
    font-size:13px; font-weight:600; letter-spacing:.12em; text-transform:uppercase;
    color:var(--blue-dark); margin-bottom:18px; display:flex; align-items:center; gap:10px;
  }
  .blog-root .eyebrow::before{content:''; width:7px; height:7px; background:var(--blue); border-radius:50%; display:inline-block;}
  .blog-root .blog-hero h1{
    font-size:clamp(34px, 5vw, 54px); line-height:1.04; letter-spacing:-0.02em; font-weight:800; margin:0 0 16px; max-width:720px;
  }
  .blog-root .blog-hero p{font-size:18px; line-height:1.55; color:#3a3d42; max-width:560px; margin:0;}

  /* covers (image-free, brand gradient) */
  .blog-root .cover{
    position:relative; aspect-ratio:16/9; border-radius:12px; overflow:hidden;
    background:linear-gradient(135deg, var(--accent, #0072F5), #0B0D10);
  }
  .blog-root .cover::after{
    content:''; position:absolute; inset:0;
    background-image:radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px);
    background-size:15px 15px; opacity:.6;
  }
  .blog-root .cover .mark{
    position:absolute; left:20px; bottom:16px; z-index:2; color:rgba(255,255,255,0.92);
    font-family:'Newsreader',serif; font-style:italic; font-size:20px;
  }
  .blog-root .cover-img{position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:1;}

  /* shared card bits */
  .blog-root .pill{
    display:inline-block; font-size:11.5px; font-weight:700; letter-spacing:.06em; text-transform:uppercase;
    background:var(--paper-dim); color:var(--accent, var(--blue-dark)); padding:5px 11px; border-radius:999px;
  }
  .blog-root .meta{font-size:12.5px; color:#6b6e73; margin-top:0;}
  .blog-root .meta .sep{padding:0 7px; opacity:.5;}

  /* featured */
  .blog-root .featured{
    padding:34px 0 8px; display:grid; grid-template-columns:1.15fr 1fr; gap:44px; align-items:center;
  }
  .blog-root .featured .cover{aspect-ratio:16/10;}
  .blog-root .featured .f-tags{display:flex; align-items:center; gap:14px; margin-bottom:16px;}
  .blog-root .featured h2{
    font-size:clamp(26px, 3vw, 36px); line-height:1.12; letter-spacing:-0.02em; font-weight:800; margin:0 0 14px;
  }
  .blog-root .featured .excerpt{font-size:16.5px; line-height:1.6; color:#3a3d42; margin:0 0 22px;}
  .blog-root .read-more{font-size:14px; font-weight:600; color:var(--blue-dark); display:inline-flex; align-items:center; gap:7px; transition:gap .2s;}
  .blog-root .read-more:hover{gap:12px;}

  /* grid */
  .blog-root .grid-head{
    display:flex; align-items:center; justify-content:space-between;
    margin:64px 0 26px; padding-bottom:16px; border-bottom:1px solid var(--line);
  }
  .blog-root .grid-head h3{font-size:15px; font-weight:700; letter-spacing:.02em; margin:0; text-transform:uppercase;}
  .blog-root .grid-head .count{font-size:13px; color:#6b6e73;}
  .blog-root .post-grid{display:grid; grid-template-columns:repeat(3,1fr); gap:40px 32px;}
  .blog-root .card{display:flex; flex-direction:column;}
  .blog-root .card .cover{margin-bottom:16px;}
  .blog-root .card .f-tags{display:flex; align-items:center; gap:12px; margin-bottom:12px;}
  .blog-root .card h4{
    font-size:19px; line-height:1.25; letter-spacing:-0.01em; font-weight:700; margin:0 0 10px;
    transition:color .2s;
  }
  .blog-root .card:hover h4{color:var(--blue-dark);}
  .blog-root .card .excerpt{
    font-size:14.5px; line-height:1.55; color:#52555a; margin:0 0 16px;
    display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden;
  }
  .blog-root .card .read-more{margin-top:auto;}

  /* pagination */
  .blog-root .pager{display:flex; justify-content:center; align-items:center; gap:8px; margin:56px 0 8px;}
  .blog-root .pager button{
    min-width:40px; height:40px; padding:0 12px; border:1px solid var(--line); background:transparent;
    border-radius:8px; font-family:'Inter',sans-serif; font-size:14px; font-weight:600; color:var(--ink);
    cursor:pointer; transition:all .15s;
  }
  .blog-root .pager button:hover:not(:disabled){border-color:var(--ink);}
  .blog-root .pager button.active{background:var(--ink); color:var(--paper); border-color:var(--ink);}
  .blog-root .pager button:disabled{opacity:.4; cursor:not-allowed;}

  /* newsletter */
  .blog-root .newsletter{margin:80px 0 0; background:var(--paper-dim); border-top:1px solid var(--line); border-bottom:1px solid var(--line);}
  .blog-root .newsletter .inner{max-width:640px; margin:0 auto; padding:72px 32px; text-align:center;}
  .blog-root .newsletter h2{font-size:clamp(28px,4vw,42px); font-weight:400; letter-spacing:-0.01em; margin:0 0 10px;}
  .blog-root .newsletter p{font-size:16px; color:#52555a; margin:0 0 28px;}
  .blog-root .news-form{display:flex; gap:10px; max-width:480px; margin:0 auto;}
  .blog-root .news-form input{
    flex:1; font-family:'Inter',sans-serif; font-size:15px; padding:14px 16px;
    border:1px solid var(--line); border-radius:999px; background:#fff; color:var(--ink);
  }
  .blog-root .news-form input:focus{outline:none; border-color:var(--blue);}
  .blog-root .news-form button{
    background:var(--blue); color:#fff; border:none; cursor:pointer; font-weight:700; font-size:15px;
    padding:14px 28px; border-radius:999px; white-space:nowrap; transition:background .2s, opacity .2s;
  }
  .blog-root .news-form button:hover:not(:disabled){background:var(--blue-dark);}
  .blog-root .news-form button:disabled{opacity:.55; cursor:not-allowed;}
  .blog-root .news-note{font-size:13px; color:#6b6e73; margin:16px 0 0;}
  .blog-root .news-success{font-size:16px; font-weight:600; color:var(--blue-dark); margin:0;}

  .blog-root footer{padding:64px 0 32px; border-top:1px solid var(--line);}
  .blog-root .foot-cols{display:grid; grid-template-columns:1.6fr 1fr 1fr 1fr; gap:40px; padding-bottom:48px; border-bottom:1px solid var(--line); margin-bottom:24px;}
  .blog-root .foot-brand p{font-size:14px; color:#6b6e73; margin-top:14px; max-width:260px; line-height:1.55;}
  .blog-root .foot-col-title{font-size:12px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:#6b6e73; margin-bottom:16px;}
  .blog-root .foot-col a{display:block; font-size:14px; color:#52555a; padding:6px 0; transition:color .2s;}
  .blog-root .foot-col a:hover{color:var(--ink);}
  .blog-root .foot-bottom{font-size:13px; color:#6b6e73;}

  @media (max-width:860px){
    .blog-root .blog-hero{padding-top:172px;}
    .blog-root .featured{grid-template-columns:1fr; gap:22px; padding-top:24px;}
    .blog-root .featured .cover{order:-1;}
    .blog-root .post-grid{grid-template-columns:1fr; gap:36px;}
    .blog-root .foot-cols{grid-template-columns:1fr; gap:32px;}
    .blog-root .newsletter .inner{padding:52px 24px;}
    .blog-root .news-form{flex-direction:column;}
    .blog-root .news-form button{width:100%;}

    .blog-root nav{flex-wrap:wrap; row-gap:14px;}
    .blog-root .nav-toggle{display:flex; flex-direction:column; justify-content:center; gap:5px; order:2; margin-left:auto;}
    .blog-root .nav-actions{order:3;}
    .blog-root .nav-links{
      display:none; order:4; width:100%; flex-direction:column; gap:0;
      border-top:1px solid var(--line); padding-top:8px;
    }
    .blog-root .nav-links.open{display:flex;}
    .blog-root .nav-links a{padding:14px 0; border-bottom:1px solid var(--line); opacity:1;}
  }

  @media (max-width:600px){
    .blog-root .blog-hero h1{font-size:32px;}
  }
`;

const Cover = ({ accent, mark, image, alt }) => (
  <div className="cover" style={{ '--accent': accent }}>
    {image
      ? <img className="cover-img" src={image} alt={alt || ''} loading="lazy" />
      : (mark && <span className="mark">{mark}</span>)}
  </div>
);

const Blog = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const posts = sortedPosts();
  const featured = posts[0];
  const rest = posts.slice(1);

  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rest.length / PER_PAGE));
  const pageItems = rest.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const goto = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [email, setEmail] = useState('');
  const [newsStatus, setNewsStatus] = useState('idle'); // idle | submitting | success | error
  const subscribe = async (e) => {
    e.preventDefault();
    setNewsStatus('submitting');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email, formType: 'newsletter', _subject: 'New newsletter subscriber (DataBackfill)' }),
      });
      setNewsStatus(res.ok ? 'success' : 'error');
    } catch {
      setNewsStatus('error');
    }
  };

  return (
    <div className="blog-root">
      <style>{css}</style>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
      <link
        href="https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;1,6..72,400&family=Inter:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <header>
        <nav>
          <Link to="/" className="logo">Data<span>Backfill</span></Link>
          <button
            className="nav-toggle"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span></span><span></span><span></span>
          </button>
          <div className="nav-actions">
            <Link to="/auth" className="nav-login">Log in</Link>
            <Link to="/contact" className="nav-cta">Start a Project</Link>
          </div>
          <div className={`nav-links${menuOpen ? ' open' : ''}`}>
            <Link to="/#services" onClick={closeMenu}>Services</Link>
            <Link to="/#work" onClick={closeMenu}>Case Studies</Link>
            <Link to="/#process" onClick={closeMenu}>How we work</Link>
            <Link to="/#team" onClick={closeMenu}>Team</Link>
            <Link to="/#pricing" onClick={closeMenu}>Pricing</Link>
          </div>
        </nav>
      </header>

      <main>
        <section className="blog-hero wrap">
          <div className="eyebrow">Blog</div>
          <h1>Field notes from the studio</h1>
          <p>Practical write-ups on shipping AI agents, RAG systems, and data infrastructure into production. No fluff, just what worked.</p>
        </section>

        {featured && (
          <section className="wrap">
            <Link to={`/blog/${featured.slug}`} className="featured">
              <Cover accent={featured.accent} mark={featured.category} image={featured.image} alt={featured.title} />
              <div>
                <div className="f-tags">
                  <span className="pill" style={{ color: featured.accent }}>{featured.category}</span>
                  <span className="meta">{formatDate(featured.date)}<span className="sep">·</span>{featured.readTime} min read</span>
                </div>
                <h2>{featured.title}</h2>
                <p className="excerpt">{featured.excerpt}</p>
                <span className="read-more">Read article
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
              </div>
            </Link>
          </section>
        )}

        {rest.length > 0 && (
          <section className="wrap">
            <div className="grid-head">
              <h3>All articles</h3>
              <span className="count">{rest.length} {rest.length === 1 ? 'post' : 'posts'}</span>
            </div>

            <div className="post-grid">
              {pageItems.map((post) => (
                <Link to={`/blog/${post.slug}`} className="card" key={post.slug}>
                  <Cover accent={post.accent} mark={post.category} image={post.image} alt={post.title} />
                  <div className="f-tags">
                    <span className="pill" style={{ color: post.accent }}>{post.category}</span>
                  </div>
                  <h4>{post.title}</h4>
                  <p className="excerpt">{post.excerpt}</p>
                  <span className="meta" style={{ marginBottom: 12 }}>{formatDate(post.date)}<span className="sep">·</span>{post.readTime} min read</span>
                  <span className="read-more">Read more
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                </Link>
              ))}
            </div>

            {pageCount > 1 && (
              <div className="pager">
                <button onClick={() => goto(page - 1)} disabled={page === 1} aria-label="Previous page">Prev</button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).map((p) => (
                  <button key={p} className={p === page ? 'active' : ''} onClick={() => goto(p)}>{p}</button>
                ))}
                <button onClick={() => goto(page + 1)} disabled={page === pageCount} aria-label="Next page">Next</button>
              </div>
            )}
          </section>
        )}

        <section className="newsletter">
          <div className="inner">
            <h2 className="serif">Get the field notes</h2>
            {newsStatus === 'success' ? (
              <p className="news-success">You're in. Look out for our next write-up.</p>
            ) : (
              <>
                <p>New posts on shipping production AI, roughly every couple of weeks. No spam.</p>
                <form className="news-form" onSubmit={subscribe}>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    aria-label="Email address"
                  />
                  <button type="submit" disabled={newsStatus === 'submitting'}>
                    {newsStatus === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                  </button>
                </form>
                {newsStatus === 'error' && <p className="news-note" style={{ color: '#c0392b' }}>Something went wrong. Please try again.</p>}
                {newsStatus === 'idle' && <p className="news-note">Join the list. Unsubscribe anytime.</p>}
              </>
            )}
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap foot-cols">
          <div className="foot-brand">
            <div className="logo">Data<span>Backfill</span></div>
            <p>A boutique AI & data delivery studio: senior engineers, architects, and product designers shipping production systems.</p>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Services</div>
            <Link to="/#services">Claude Implementation</Link>
            <Link to="/#services">RAG & Knowledge Systems</Link>
            <Link to="/#services">Agentic Workflows</Link>
            <Link to="/#services">Data Infrastructure</Link>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Company</div>
            <Link to="/#work">Case Studies</Link>
            <Link to="/#team">Team</Link>
            <Link to="/#pricing">Pricing</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div className="foot-col">
            <div className="foot-col-title">Use Cases</div>
            <Link to="/use-cases/cmo">25 Use Cases for CMOs</Link>
            <Link to="/use-cases/marketing-ai">25 Marketing AI Use Cases</Link>
            <Link to="/use-cases/ai-bdr">25 AI BDR Use Cases</Link>
            <Link to="/use-cases/cfo">15 Use Cases for CFOs</Link>
            <Link to="/use-cases/customer-support">20 AI Customer Support Use Cases</Link>
            <Link to="/use-cases/revops">12 RevOps AI Use Cases</Link>
            <Link to="/use-cases/hr">10 AI Use Cases for HR Teams</Link>
            <Link to="/use-cases/data-teams">18 Use Cases for Data Teams</Link>
          </div>
        </div>
        <div className="wrap foot-bottom">
          <div>© 2026 DataBackfill. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
